/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { convertResponseRow } from '@app/sql/helpers/convert-response-row.helper';
import { ClusterActionQueryResponseResult } from '@shared/types/queries';
import { ConvertedQueryResponseResult } from '../types';

const joinKeyValue = (it: { [x: string]: string | number | boolean }): string =>
  Object.keys(it)
    .map((k) => `${k}:${it[k]}`)
    .join('|');

const pushConvertedValue = (
  item: ClusterActionQueryResponseResult,
  counter: number,
  list: ClusterActionQueryResponseResult[],
) => {
  if (item !== null) {
    if (item.title) {
      const statements = counter !== 0 ? `[${counter + 1}]\n` : '';
      const title = formatTitle(item.title, counter);
      item.title = `${statements}${title}`;
    }
    list.push(item);
  }
};

const formatTitle = (title: string, counter: number) => {
  const length = title.length;
  if (length > 200) {
    const start = title.slice(0, 100);
    const end = title.slice(length - 100, length);
    const delimeter = counter !== 0 ? '…\n…' : '…';
    return `${start}${delimeter}${end}`;
  }
  return title;
};

export const getStackedResults = <T extends ClusterActionQueryResponseResult | ConvertedQueryResponseResult>(
  result: T[],
): T[] => {
  let iterator = 0;
  let item: T | null = null;
  const stackedResults: T[] = [];
  result.forEach((current, index, list) => {
    if (item === null) {
      item = current;
    } else {
      const prev = list[index - 1];
      if (prev) {
        const hasCorrectRowCount = prev.rows.length === 1 && prev.rows.length === current.rows.length;
        const prevContent = convertResponseRow(prev).map(joinKeyValue).join();
        const currentContent = convertResponseRow(current).map(joinKeyValue).join();
        if (hasCorrectRowCount && prevContent === currentContent) {
          iterator++;
          item.title = [item.title, current.title].join('\n');
        } else {
          pushConvertedValue(item, iterator, stackedResults);
          iterator = 0;
          item = current;
        }
      }
    }

    const isLast = index === list.length - 1;
    if (isLast) {
      pushConvertedValue(item, iterator, stackedResults);
    }
  });
  return stackedResults;
};
